import React, { useEffect, useRef } from "react";
import useState from "react-usestateref";
import { useUserMedia, MediaError } from "../hooks/use-user-media";
import { useQr } from "../hooks/use-qr";
import { beep, ensureAudio, monochromize, vibrate } from "../helper";
import { canvasColor } from "../theme";
import {
  Canvas,
  Container,
  Overlay,
  OverlayAction,
  OverlayText,
  OverlayTitle,
  Video,
  Wrapper,
} from "./styles";

/** Drawing buffer of the viewfinder. Reticle geometry is in these pixels. */
const FRAME = { width: 320, height: 430 };

/** The region handed to the decoders, per crosshair style. */
const RETICLE = {
  square: { x0: 32, y0: 87, width: 256, height: 256 },
  rect: { x0: 11, y0: 119, width: 298, height: 192 },
};

const reticleFor = (style) => RETICLE[style] || RETICLE.square;

/** Corner bracket geometry. Thin strokes and a sharp radius, never a flourish. */
const BRACKET = { length: 28, width: 3, radius: 4 };

/**
 * Copies the current video frame into the canvas, scaled to cover.
 *
 * The previous implementation copied a 320x430 window out of the native frame
 * at 1:1 pixel scale, so the framing and the zoom level changed with every
 * camera resolution: a 1080p stream showed a 17% wide centre sliver that no QR
 * code could ever fit into. Scaling to cover makes the viewfinder behave the
 * same whatever resolution the browser hands back.
 */
function drawVideoFrame(video, canvas) {
  const vWidth = video.videoWidth;
  const vHeight = video.videoHeight;
  if (!vWidth || !vHeight) return false;

  const context = canvas.getContext("2d", { willReadFrequently: true });
  const scale = Math.max(canvas.width / vWidth, canvas.height / vHeight);
  const sourceWidth = canvas.width / scale;
  const sourceHeight = canvas.height / scale;

  context.drawImage(
    video,
    (vWidth - sourceWidth) / 2,
    (vHeight - sourceHeight) / 2,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return true;
}

/** One L-shaped bracket, opening towards (dx, dy). */
function traceBracket(context, x, y, dx, dy) {
  const { length, radius } = BRACKET;
  context.moveTo(x, y + dy * length);
  context.lineTo(x, y + dy * radius);
  context.quadraticCurveTo(x, y, x + dx * radius, y);
  context.lineTo(x + dx * length, y);
}

/**
 * Dims everything outside the reticle and strokes the four corner brackets.
 *
 * Must run after the frame has been read for decoding: the brackets sit inside
 * the decoded region, so painting them first overwrote the corners of the image
 * the decoders were given, exactly where a QR code keeps its finder patterns.
 */
export function drawCrosshair(canvas, style) {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  const { x0, y0, width, height } = reticleFor(style);
  const x1 = x0 + width;
  const y1 = y0 + height;

  context.fillStyle = canvasColor.mask;
  context.fillRect(0, 0, canvas.width, y0);
  context.fillRect(0, y1, canvas.width, canvas.height - y1);
  context.fillRect(0, y0, x0, height);
  context.fillRect(x1, y0, canvas.width - x1, height);

  context.strokeStyle = canvasColor.reticle;
  context.lineWidth = BRACKET.width;
  context.lineCap = "square";
  context.beginPath();
  traceBracket(context, x0, y0, 1, 1);
  traceBracket(context, x1, y0, -1, 1);
  traceBracket(context, x0, y1, 1, -1);
  traceBracket(context, x1, y1, -1, -1);
  context.stroke();
}

export function Scan({
  onCapture,
  onClear,
  onCameraError,
  crosshair = { enabled: true, style: "square" },
  beepOn = true,
  scanRate = 250,
  bw = true,
  resumeToken = 0,
}) {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const frameRef = useRef(0);
  const lastScanRef = useRef(0);
  const tickRef = useRef(() => {});

  const [isPlaying, setIsPlaying, playingRef] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  // Distinguishes "not started yet" from "stopped on a capture", where the last
  // drawn frame stays on the canvas as a record of what was read.
  const [isCaptured, setIsCaptured] = useState(false);

  const { stream, error, isRequesting, request } = useUserMedia();
  const [zbarWorker, zxingWorker] = useQr(handleCapture);

  // The animation loop is long lived, so anything it touches is read through a
  // ref rather than captured from the render that started it.
  const workersRef = useRef([null, null]);
  workersRef.current = [zbarWorker, zxingWorker];

  function stopLoop() {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = 0;
  }

  function startLoop() {
    if (frameRef.current) return;
    const step = (time) => {
      frameRef.current = requestAnimationFrame(step);
      tickRef.current(time);
    };
    frameRef.current = requestAnimationFrame(step);
  }

  function tick(time) {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas || !playingRef.current) {
      stopLoop();
      return;
    }

    // HAVE_CURRENT_DATA is enough to copy a frame. Waiting for
    // HAVE_ENOUGH_DATA stalled forever on WebViews that never promote the
    // stream past HAVE_FUTURE_DATA.
    if (video.readyState < 2) return;
    if (!drawVideoFrame(video, canvas)) return;

    if (bw) monochromize(canvas);
    if (time - lastScanRef.current > scanRate) {
      lastScanRef.current = time;
      decodeFrame(canvas);
    }
    if (crosshair.enabled) drawCrosshair(canvas, crosshair.style);
  }

  tickRef.current = tick;

  function decodeFrame(canvas) {
    const [zbar, zxing] = workersRef.current;
    if (!zbar || !zxing) return;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    const region = crosshair.enabled
      ? reticleFor(crosshair.style)
      : { x0: 0, y0: 0, width: canvas.width, height: canvas.height };

    // One message per decoder. ImageData already carries width, height and
    // data, so the previous dimensions-then-pixels pair was redundant and left
    // the workers holding dimensions that could belong to another frame.
    const imageData = context.getImageData(
      region.x0,
      region.y0,
      region.width,
      region.height
    );

    zbar.postMessage(imageData);
    zxing.postMessage(imageData);
  }

  function handleCapture(code) {
    if (!videoRef.current || !playingRef.current) return;
    if (beepOn) beep();
    vibrate();
    videoRef.current.pause();
    setIsPlaying(false);
    setIsCaptured(true);
    stopLoop();
    onCapture(code);
    if (onClear) onClear();
  }

  async function play() {
    const video = videoRef.current;
    if (!video) return;

    try {
      await video.play();
      setNeedsGesture(false);
      setIsCaptured(false);
      setIsPlaying(true);
      startLoop();
    } catch (err) {
      // WebViews refuse programmatic playback outside a user activation
      // window. Offer the tap that satisfies them.
      setNeedsGesture(true);
    }
  }

  // Ask for the camera on mount. A refusal is recoverable: the overlay retry
  // runs the same request from inside a real user gesture.
  useEffect(() => {
    request();
  }, [request]);

  useEffect(() => {
    if (!stream || !videoRef.current) return;
    const video = videoRef.current;
    if (video.srcObject !== stream) video.srcObject = stream;
    play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  // Restart after a capture without asking for the camera a second time.
  useEffect(() => {
    if (!resumeToken || !stream) return;
    lastScanRef.current = 0;
    play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeToken]);

  useEffect(() => stopLoop, []);

  // Reported unconditionally, so a successful retry clears the error upstream
  // instead of leaving the parent stuck on a failure that no longer applies.
  useEffect(() => {
    if (onCameraError) onCameraError(error);
  }, [error, onCameraError]);

  const handleGesture = () => {
    ensureAudio();
    if (error) request();
    else play();
  };

  const overlay = renderOverlay({
    error,
    isRequesting,
    isPlaying,
    isCaptured,
    needsGesture,
    onAction: handleGesture,
  });

  return (
    <Wrapper>
      <Container>
        <Video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          webkit-playsinline="true"
        />
        <Canvas ref={canvasRef} width={FRAME.width} height={FRAME.height} />
        {overlay}
      </Container>
    </Wrapper>
  );
}

const CAMERA_ERROR_COPY = {
  [MediaError.UNSUPPORTED]: {
    title: "Este navegador não abre a câmera",
    text:
      "Navegadores internos de carteiras podem bloquear a câmera. Abra o link no Chrome ou no Safari para escanear.",
    action: null,
  },
  [MediaError.INSECURE]: {
    title: "Conexão insegura",
    text: "O leitor precisa de uma conexão HTTPS para acessar a câmera.",
    action: null,
  },
  [MediaError.DENIED]: {
    title: "Acesso à câmera negado",
    text:
      "Libere a permissão de câmera para este site e toque em tentar novamente.",
    action: "Tentar novamente",
  },
  [MediaError.NOT_FOUND]: {
    title: "Nenhuma câmera encontrada",
    text: "Não localizamos uma câmera disponível neste dispositivo.",
    action: "Tentar novamente",
  },
  [MediaError.IN_USE]: {
    title: "Câmera ocupada",
    text:
      "Outro aplicativo está usando a câmera. Feche o outro aplicativo e tente novamente.",
    action: "Tentar novamente",
  },
  [MediaError.UNKNOWN]: {
    title: "Não foi possível iniciar a câmera",
    text: "Tente novamente ou abra o link em outro navegador.",
    action: "Tentar novamente",
  },
};

function renderOverlay({
  error,
  isRequesting,
  isPlaying,
  isCaptured,
  needsGesture,
  onAction,
}) {
  if (error) {
    const copy = CAMERA_ERROR_COPY[error] || CAMERA_ERROR_COPY[MediaError.UNKNOWN];
    return (
      <Overlay>
        <OverlayTitle>{copy.title}</OverlayTitle>
        <OverlayText>{copy.text}</OverlayText>
        {copy.action && (
          <OverlayAction type="button" onClick={onAction}>
            {copy.action}
          </OverlayAction>
        )}
      </Overlay>
    );
  }

  if (needsGesture) {
    return (
      <Overlay>
        <OverlayTitle>Toque para iniciar a câmera</OverlayTitle>
        <OverlayText>
          Este navegador exige um toque antes de exibir a câmera.
        </OverlayText>
        <OverlayAction type="button" onClick={onAction}>
          Iniciar câmera
        </OverlayAction>
      </Overlay>
    );
  }

  // Nothing over a captured frame: the frozen image is the confirmation.
  if (!isPlaying && !isCaptured) {
    return (
      <Overlay>
        <OverlayTitle>
          {isRequesting
            ? "Aguardando permissão da câmera"
            : "Iniciando a câmera"}
        </OverlayTitle>
        <OverlayText>Aponte a câmera para o QR Code.</OverlayText>
      </Overlay>
    );
  }

  return null;
}
